import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchPetFindersAPI,
  fetchPetFinderByIdAPI,
  deletePetFinderAPI,
  updatePetFinderAPI,
  createPetFinderAPI,
} from "../../services/petFinderAPI";



// ✅ Fetch all pet finders with pagination
export const fetchPetFinders = createAsyncThunk(
  "petFinder/fetchPetFinders",
  async (page = 1) => {
    const response = await fetchPetFindersAPI(page);
    return { data: response.data.data, pagination: response.data.pagination };
  }
);

// ✅ Fetch single pet finder by ID
export const fetchPetFinderById = createAsyncThunk(
  "petFinder/fetchPetFinderById",
  async (id) => {
    const response = await fetchPetFinderByIdAPI(id);
    return response.data;
  }
);

// ✅ Delete pet finder
export const deletePetFinder = createAsyncThunk(
  "petFinder/deletePetFinder",
  async (id) => {
    await deletePetFinderAPI(id);
    return id;
  }
);

// ✅ Update pet finder
export const updatePetFinder = createAsyncThunk(
  "petFinder/updatePetFinder",
  async ({ id, data }) => {
    const response = await updatePetFinderAPI(id, data);
    return response.data;
  }
);

// ✅ Create new pet finder
export const createPetFinder = createAsyncThunk(
  "petFinder/createPetFinder",
  async (data) => {
    const response = await createPetFinderAPI(data);
    return response.data;
  }
);

const initialState = {
  items: [],
  detail: null,
  status: "idle",        // for list (fetchPetFinders)
  detailStatus: "idle",  // for fetchPetFinderById
  error: null,
  // Pagination state
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
};

const petFinderSlice = createSlice({
  name: "petFinder",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchPetFinders.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPetFinders.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { data, pagination } = action.payload;
        const petFinders = data.pet_finders;
        state.items = petFinders.map(pf => ({
          uuid: pf.uuid,
          pet: `${pf.breed || 'NA'} - ${pf.pet_name || 'NA'}`,
          location: pf.last_known_location || 'NA',
          name: pf.user_details?.name || 'NA',
          reward: pf.reward || 'NA',
          status: pf.active ? 'Active' : 'Inactive',
          submitted: pf.created_at,
          mobile: pf.user_details?.mobile || 'NA',
          email: pf.user_details?.email || 'NA',
          description: pf.additional_info || 'NA',
          sex: pf.sex || 'NA',
          age: pf.age || 'NA',
          physical_marks: pf.physical_marks || 'NA',
          color: pf.color || 'NA',
          images: pf.images || [],
        }));
        // Update pagination
        if (pagination) {
          state.pagination = {
            currentPage: pagination.current_page || 1,
            totalPages: pagination.total_pages || 1,
            totalItems: pagination.total_count || 0,
            itemsPerPage: pagination.per_page || 10
          };
        }
      })
      .addCase(fetchPetFinders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single
      .addCase(fetchPetFinderById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchPetFinderById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload.data;
      })
      .addCase(fetchPetFinderById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete
      .addCase(deletePetFinder.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((p) => p.uuid !== id);
        if (state.detail?.uuid === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // ✅ Update
      .addCase(updatePetFinder.fulfilled, (state, action) => {
        state.detail = action.payload; // refresh detail
        state.items = state.items.map((p) =>
          p.uuid === action.meta.arg.id ? action.payload : p
        );
      })

      // ✅ Create
      .addCase(createPetFinder.fulfilled, (state, action) => {
        state.items.push(action.payload);
      });
  },
});

export const { clearDetail } = petFinderSlice.actions;
export default petFinderSlice.reducer;
